import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../../ApiUrl";
import {
  createEmptyEmergencyContact,
  createEmptyMedicalFitness,
} from "../constants";

const CONTACT_URL = `${API_BASE}/contact-records/`;
const COMBINED_CREDENTIAL_URL = `${API_BASE}/combined-credential-record/`;
const CREDENTIAL_URL = `${API_BASE}/credential-records/`;
const PROFILE_URL = `${API_BASE}/professional-profiles/`;

export const useDeploymentRecords = (professionalProfileId, tenantId) => {
  const [emergencyContactRecords, setEmergencyContactRecords] = useState([
    createEmptyEmergencyContact(),
  ]);
  const [medicalFitnessRecords, setMedicalFitnessRecords] = useState([
    createEmptyMedicalFitness(),
  ]);
  const [ppeSizes, setPpeSizes] = useState({
    helmet_size: "",
    safety_boot_size: "",
    coverall_size: "",
    additional_requirements: "",
  });
  const [declarationData, setDeclarationData] = useState({
    accuracy: false,
    conflict_of_interest: false,
    conflict_details: "",
  });

  // ----- Phone validation helpers -----
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === "") return true;
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone.trim());
  };

  const formatPhoneToE164 = (phone) => {
    if (!phone) return "";
    let cleaned = phone.trim();
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    if (!cleaned.startsWith("+")) {
      cleaned = "+91" + cleaned;
    }
    return cleaned;
  };

  // ----- Backend-id helpers -----
  const extractContactBackendId = (recordId) => {
    const match = /^contact-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  const extractMedicalBackendId = (recordId) => {
    const match = /^medical-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  // ----- Hydration -----
  const hydrateEmergencyContactsFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      setEmergencyContactRecords([createEmptyEmergencyContact()]);
      return;
    }

    const filtered = records.filter(
      (rec) => rec.contact_type === "EMERGENCY_CONTACT",
    );

    if (filtered.length === 0) {
      setEmergencyContactRecords([createEmptyEmergencyContact()]);
      return;
    }

    setEmergencyContactRecords(
      filtered.map((rec) => ({
        id: `contact-${rec.id}`,
        full_name: rec.full_name || "",
        relationship: rec.relationship || "",
        phone: rec.phone || "",
        email: rec.email || "",
        errors: {},
      })),
    );
  };

  const hydrateMedicalFitnessFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      setMedicalFitnessRecords([createEmptyMedicalFitness()]);
      return;
    }

    const filtered = records.filter(
      (rec) => rec.record_type === "MEDICAL_FITNESS",
    );

    if (filtered.length === 0) {
      setMedicalFitnessRecords([createEmptyMedicalFitness()]);
      return;
    }

    setMedicalFitnessRecords(
      filtered.map((rec) => ({
        id: `medical-${rec.id}`,
        fitness_type: rec.level_or_grade || "",
        provider: rec.issuing_body_snapshot || "",
        status: rec.status || "",
        issue_date: rec.issue_date || "",
        expiry_date: rec.expiry_date || "",
        restrictions: rec.restrictions_or_limitations || "",
        errors: {},
      })),
    );
  };

  const hydratePpeSizesFromProfile = (profile) => {
    if (!profile) return;
    if (profile.ppe_sizes) {
      try {
        const parsed =
          typeof profile.ppe_sizes === "string"
            ? JSON.parse(profile.ppe_sizes)
            : profile.ppe_sizes;
        setPpeSizes({
          helmet_size: parsed.helmet_size || "",
          safety_boot_size: parsed.safety_boot_size || "",
          coverall_size: parsed.coverall_size || "",
          additional_requirements: parsed.additional_requirements || "",
        });
      } catch (err) {
        console.error("Failed to parse ppe_sizes", err);
      }
    }
  };

  // Hydrate from related records - NEW METHOD
  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;
    
    // Hydrate emergency contacts from contacts
    if (data.contacts && Array.isArray(data.contacts)) {
      hydrateEmergencyContactsFromRecords(data.contacts);
    }
    
    // Also check for emergency contacts in references (some APIs might put them there)
    if (data.references && Array.isArray(data.references)) {
      // Only use if contacts didn't have data
      if (!data.contacts || data.contacts.length === 0) {
        hydrateEmergencyContactsFromRecords(data.references);
      }
    }
    
    // Hydrate medical fitness from credentials
    if (data.credentials && Array.isArray(data.credentials)) {
      hydrateMedicalFitnessFromRecords(data.credentials);
    }
    
    // Hydrate PPE sizes from profile data
    if (data.ppe_sizes) {
      hydratePpeSizesFromProfile(data);
    }
    
    // Also check if profile object has ppe_sizes
    if (data.profile && data.profile.ppe_sizes) {
      hydratePpeSizesFromProfile(data.profile);
    }
    
    // Check for declaration data in the response (consent records)
    if (data.consent_records && Array.isArray(data.consent_records)) {
      const accuracyConsent = data.consent_records.find(
        (c) => c.consent_type === "PROFILE_ACCURACY"
      );
      const conflictConsent = data.consent_records.find(
        (c) => c.consent_type === "CONFLICT_OF_INTEREST"
      );
      
      setDeclarationData({
        accuracy: accuracyConsent ? accuracyConsent.is_granted : false,
        conflict_of_interest: conflictConsent ? conflictConsent.is_granted : false,
        conflict_details: "", // You might need to get this from somewhere else
      });
    }
  };

  // Fetch from related-records endpoint only - UPDATED
  useEffect(() => {
    if (!professionalProfileId) return;

    const loadDeploymentData = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`
        );
        const data = res?.data?.data || res?.data;
        
        // Hydrate all deployment data
        hydrateFromRelatedRecords(data);
      } catch (err) {
        console.error('Failed to load deployment data from related-records API', err);
        // Keep the default empty states
      }
    };

    loadDeploymentData();
  }, [professionalProfileId]);

  // ----- Payload builders -----
  const buildContactPayload = (rec) => ({
    resume_visibility: "NEVER",
    data_classification: "PUBLIC",
    contact_type: "EMERGENCY_CONTACT",
    full_name: rec.full_name || "",
    job_title: "",
    organization_other: "",
    organization_name_snapshot: "",
    relationship: rec.relationship || "",
    email: rec.email || "",
    phone: rec.phone ? formatPhoneToE164(rec.phone) : "",
    consent_given: true,
    consent_given_at: new Date().toISOString(),
    verification_status: "SELF_DECLARED",
    tenant: tenantId || '',
    professional: professionalProfileId,
  });

  const buildMedicalPayload = (rec) => ({
    record_type: "MEDICAL_FITNESS",
    title: rec.fitness_type || "",
    issuing_body_snapshot: rec.provider || "",
    level_or_grade: rec.fitness_type || "",
    issue_date: rec.issue_date || null,
    expiry_date: rec.expiry_date || null,
    status: rec.status || "DRAFT",
    restrictions_or_limitations: rec.restrictions || "",
    verification_status: "SELF_DECLARED",
    resume_visibility: "NEVER",
    data_classification: "PUBLIC",
    tenant: tenantId || '',
    professional: professionalProfileId,
    items: [],
  });

  // ----- Submit: emergency contacts -----
  const submitEmergencyContacts = async () => {
    if (!professionalProfileId)
      throw new Error("Professional profile ID missing.");

    const toSubmit = emergencyContactRecords.filter((rec) =>
      rec.full_name.trim(),
    );

    const validationErrors = [];
    toSubmit.forEach((rec, index) => {
      if (rec.phone && !validatePhoneNumber(rec.phone)) {
        validationErrors.push(
          `Emergency contact ${index + 1}: Phone must be in E.164 format (e.g., +14155552671)`,
        );
      }
    });

    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join("\n"));
    }

    await Promise.all(
      toSubmit.map((rec) => {
        const backendId = extractContactBackendId(rec.id);
        const payload = buildContactPayload(rec);
        return backendId
          ? axios.put(`${CONTACT_URL}${backendId}/`, payload)
          : axios.post(CONTACT_URL, payload);
      }),
    );
  };

  // ----- Submit: medical fitness -----
  const submitMedicalFitness = async () => {
    if (!professionalProfileId)
      throw new Error("Professional profile ID missing.");

    const toSubmit = medicalFitnessRecords.filter((rec) =>
      rec.fitness_type.trim(),
    );

    if (toSubmit.length === 0) return;

    const payloads = toSubmit.map((rec) => {
      const backendId = extractMedicalBackendId(rec.id);
      return {
        id: backendId ? parseInt(backendId) : null,
        ...buildMedicalPayload(rec),
      };
    });

    const existingRecords = payloads.filter((p) => p.id !== null);
    const newRecords = payloads.filter((p) => p.id === null);

    try {
      if (existingRecords.length > 0) {
        await Promise.all(
          existingRecords.map((payload) => {
            const { id, ...data } = payload;
            return axios.put(`${COMBINED_CREDENTIAL_URL}${id}/`, data);
          }),
        );
      }

      if (newRecords.length > 0) {
        const newPayloads = newRecords.map(({ id, ...data }) => data);
        await axios.post(COMBINED_CREDENTIAL_URL, newPayloads);
      }
    } catch (error) {
      console.error("Failed to submit medical fitness records", error);
      throw error;
    }
  };

  // ----- Submit: PPE sizes -----
  const submitPpeSizes = async () => {
    if (!professionalProfileId)
      throw new Error("Professional profile ID missing.");
    await axios.put(`${PROFILE_URL}${professionalProfileId}/`, {
      ppe_sizes: JSON.stringify(ppeSizes),
    });
  };

  // ----- Delete handlers -----
  const removeEmergencyContactRecord = (index) => {
    const record = emergencyContactRecords[index];
    const backendId = record ? extractContactBackendId(record.id) : null;

    setEmergencyContactRecords((prev) => {
      if (prev.length === 1) return [createEmptyEmergencyContact()];
      return prev.filter((_, i) => i !== index);
    });

    if (backendId) {
      axios.delete(`${CONTACT_URL}${backendId}/`).catch((err) => {
        console.error("Failed to delete emergency contact", err);
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: "Could not delete this record from the server.",
        });
      });
    }
  };

  const removeMedicalFitnessRecord = (index) => {
    const record = medicalFitnessRecords[index];
    const backendId = record ? extractMedicalBackendId(record.id) : null;

    setMedicalFitnessRecords((prev) => {
      if (prev.length === 1) return [createEmptyMedicalFitness()];
      return prev.filter((_, i) => i !== index);
    });

    if (backendId) {
      axios.delete(`${CREDENTIAL_URL}${backendId}/`).catch((err) => {
        console.error("Failed to delete medical fitness record", err);
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: "Could not delete this record from the server.",
        });
      });
    }
  };

  // ----- Combined submits -----
  const submitAll = async () => {
    await submitEmergencyContacts();
    await submitMedicalFitness();
    await submitPpeSizes();
  };

  const submitDeploymentOnly = async () => {
    await submitEmergencyContacts();
    await submitMedicalFitness();
    await submitPpeSizes();
  };

  // ----- Validation -----
  const validate = () => {
    const errors = {};

    if (!declarationData.accuracy) {
      errors.accuracy = "You must confirm the accuracy of your profile";
    }

    emergencyContactRecords.forEach((rec, index) => {
      if (rec.full_name && rec.full_name.trim()) {
        if (!rec.phone || rec.phone.trim() === "") {
          errors[`emergency_phone_${index}`] =
            "Phone number is required for emergency contact";
        } else if (!validatePhoneNumber(rec.phone)) {
          errors[`emergency_phone_${index}`] =
            "Phone number must be in E.164 format (e.g., +14155552671)";
        }
      }
    });

    return errors;
  };

  // ----- Record management helpers -----
  const addRecord = (setter, creator) => {
    setter((prev) => [...prev, creator()]);
  };

  const removeRecord = (setter, index, creator) => {
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRecordChange = (setter, index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return {
    emergencyContactRecords,
    medicalFitnessRecords,
    ppeSizes,
    declarationData,
    setEmergencyContactRecords,
    setMedicalFitnessRecords,
    setPpeSizes,
    setDeclarationData,
    hydrateFromRelatedRecords,
    validate,
    submitEmergencyContacts,
    submitMedicalFitness,
    submitPpeSizes,
    submitAll,
    submitDeploymentOnly,
    removeEmergencyContactRecord,
    removeMedicalFitnessRecord,
    addRecord,
    removeRecord,
    handleRecordChange,
  };
};